/**
 * i18n core
 * - Default language: English ('en')
 * - Fallback: English ('en')
 * - Detection order: URL (path/query) → ?lang= → localStorage → browser
 *
 * Locales are authored in `.properties` files with namespaced snake_case keys
 * (e.g. `game.bet`, `ui.close`, `pf.server_seed_title`). Numeric-suffix keys
 * like `rules.intro.0 / .1 / .2` are exposed as JavaScript arrays so
 * `t('ui.popuppanel.rules.intro', { returnObjects: true })` returns `string[]`.
 *
 * Usage:
 *   import { useT } from '@core/i18n';
 *   const t = useT('chickenroad'); // game ns, falls back to common
 *   <button>{t('ui.control_panel.action.play')}</button>
 */
import i18next, { type Resource } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next, useTranslation } from 'react-i18next';
import { parseLanguageFromUrl } from '@core/utils/urlParser';
import { parseProperties, type NestedDict } from './parseProperties';
import {
  DEFAULT_LANGUAGE,
  getLanguage,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from './language';

export {
  DEFAULT_LANGUAGE,
  getLanguage,
  normalizeSupportedLanguage,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from './language';

// .properties files are imported as raw strings via Vite's `?raw` query.
import commonEnRaw from './locales/en.properties?raw';
import commonEsRaw from './locales/es.properties?raw';
import commonDeRaw from './locales/de.properties?raw';
import commonFrRaw from './locales/fr.properties?raw';
import commonItRaw from './locales/it.properties?raw';
import commonElRaw from './locales/el.properties?raw';
import commonBgRaw from './locales/bg.properties?raw';
import commonDaRaw from './locales/da.properties?raw';
import commonFiRaw from './locales/fi.properties?raw';
import commonIdRaw from './locales/id.properties?raw';
import commonJaRaw from './locales/ja.properties?raw';
import commonKoRaw from './locales/ko.properties?raw';
import commonLvRaw from './locales/lv.properties?raw';
import commonLtRaw from './locales/lt.properties?raw';
import commonMsRaw from './locales/ms.properties?raw';
import commonNbRaw from './locales/nb.properties?raw';
import commonPlRaw from './locales/pl.properties?raw';
import commonPtRaw from './locales/pt.properties?raw';
import commonRoRaw from './locales/ro.properties?raw';
import commonRuRaw from './locales/ru.properties?raw';
import commonSrRaw from './locales/sr.properties?raw';
import commonSvRaw from './locales/sv.properties?raw';
import commonThRaw from './locales/th.properties?raw';
import commonTrRaw from './locales/tr.properties?raw';
import commonViRaw from './locales/vi.properties?raw';
import commonZhRaw from './locales/zh.properties?raw';
import commonZhTwRaw from './locales/zh-TW.properties?raw';

const commonByLang: Record<SupportedLanguage, NestedDict> = {
  en: parseProperties(commonEnRaw),
  es: parseProperties(commonEsRaw),
  de: parseProperties(commonDeRaw),
  fr: parseProperties(commonFrRaw),
  it: parseProperties(commonItRaw),
  el: parseProperties(commonElRaw),
  bg: parseProperties(commonBgRaw),
  da: parseProperties(commonDaRaw),
  fi: parseProperties(commonFiRaw),
  id: parseProperties(commonIdRaw),
  ja: parseProperties(commonJaRaw),
  ko: parseProperties(commonKoRaw),
  lv: parseProperties(commonLvRaw),
  lt: parseProperties(commonLtRaw),
  ms: parseProperties(commonMsRaw),
  nb: parseProperties(commonNbRaw),
  pl: parseProperties(commonPlRaw),
  pt: parseProperties(commonPtRaw),
  ro: parseProperties(commonRoRaw),
  ru: parseProperties(commonRuRaw),
  sr: parseProperties(commonSrRaw),
  sv: parseProperties(commonSvRaw),
  th: parseProperties(commonThRaw),
  tr: parseProperties(commonTrRaw),
  vi: parseProperties(commonViRaw),
  zh: parseProperties(commonZhRaw),
  'zh-TW': parseProperties(commonZhTwRaw),
};

const resources: Resource = Object.fromEntries(
  (Object.keys(commonByLang) as SupportedLanguage[]).map(lang => [
    lang,
    { common: commonByLang[lang] as Record<string, unknown> },
  ])
);

const urlLanguageDetector = {
  name: 'urlLanguage',
  lookup() {
    return parseLanguageFromUrl() ?? undefined;
  },
  cacheUserLanguage(lng: string) {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('i18nLang', lng);
    }
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(urlLanguageDetector);

const initialUrlLanguage =
  typeof window !== 'undefined' ? parseLanguageFromUrl() : null;

const i18nInitPromise = i18next
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    lng: initialUrlLanguage ?? DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES] as string[],
    nonExplicitSupportedLngs: false,
    defaultNS: 'common',
    fallbackNS: 'common',
    ns: ['common'],
    keySeparator: '.',
    nsSeparator: ':',
    interpolation: { escapeValue: false },
    detection: {
      order: ['urlLanguage', 'querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'i18nLang',
      caches: ['localStorage'],
    },
  });

export type GameLocaleBundle = Partial<
  Record<SupportedLanguage, string | NestedDict>
>;

function applyGameLocaleBundle(gameId: string, bundle: GameLocaleBundle): void {
  (Object.keys(bundle) as SupportedLanguage[]).forEach(lang => {
    const data = bundle[lang];
    if (data === undefined) return;
    const parsed: NestedDict =
      typeof data === 'string' ? parseProperties(data) : data;
    i18next.addResourceBundle(
      lang,
      gameId,
      parsed as Record<string, unknown>,
      true,
      true
    );
  });
}

/**
 * Register translations for a game. The game's namespace is the gameId.
 * Each entry may be a raw `.properties` string (recommended; pass the
 * `?raw` import) or an already-parsed nested object.
 * Keys defined here override common keys when using useT(gameId).
 */
export function registerGameLocale(
  gameId: string,
  bundle: GameLocaleBundle
): void {
  void i18nInitPromise.then(() => {
    applyGameLocaleBundle(gameId, bundle);
  });
}

/**
 * Hook returning a t() that looks up in [gameId, common] order.
 * Pass undefined for lobby / shared screens (uses common only).
 */
export function useT(gameId?: string) {
  const namespaces = gameId ? [gameId, 'common'] : ['common'];
  const { t } = useTranslation(namespaces);
  return t;
}

export function setLanguage(lang: SupportedLanguage): Promise<unknown> {
  return i18next.changeLanguage(lang);
}

/** Apply `?lang=` / path language when the URL changes (SPA navigation). */
export function applyLanguageFromUrl(): void {
  const lang = parseLanguageFromUrl();
  if (lang && lang !== getLanguage()) {
    void setLanguage(lang);
  }
}

export {
  formatAmount,
  formatCurrencyAmount,
  formatCurrencyUnit,
  formatMoney,
  formatDecimal,
  formatEditableAmount,
  formatMultiplier,
  getCurrencyNarrowSymbol,
  getNumberSeparators,
  getLanguageDecimalSeparator,
  shouldUseCurrencySymbol,
  usesCommaDecimal,
  COMMA_DECIMAL_LANGUAGE_CONFIG,
  canonicalizeLocalizedNumberInput,
  acceptLocalizedNumberInput,
  isLocalizedDecimalKey,
  isPartialLocalizedNumberInput,
  parseLocalizedNumber,
  resolveNumberLocale,
  truncateToDecimals,
  useNumberFormat,
  type NumberFormatOptions,
} from './numberFormat';

export { formatChatMessageTime } from './formatChatMessageTime';

export default i18next;
